import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Play, Pause, Volume2, Image as ImageIcon, CheckCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExerciseView() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const audioRefs = useRef([]);
  
  const [exercise, setExercise] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [exerciseStatus, setExerciseStatus] = useState('todo');

  useEffect(() => {
    loadExercise();
  }, [exerciseId]);

  const loadExercise = async () => {
    try {
      const exerciseRes = await axios.get(`${API}/exercises/${exerciseId}`);
      setExercise(exerciseRes.data);
      
      // Load lesson info
      const lessonRes = await axios.get(`${API}/lessons/${exerciseRes.data.lesson_id}`);
      setLesson(lessonRes.data);
      
      // Load user's progress for this exercise
      const progressRes = await axios.get(`${API}/users/${user.id}/progress`);
      const progress = progressRes.data.find(p => p.exercise_id === exerciseId);
      setExerciseStatus(progress?.status || 'todo');
    } catch (error) {
      console.error('Error loading exercise:', error);
      toast.error('Failed to load exercise');
    } finally {
      setLoading(false);
    }
  };

  const markAsDone = async () => {
    try {
      await axios.patch(`${API}/progress/${user.id}/${exerciseId}?status=done`);
      toast.success('Exercise marked as done!');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to mark as done');
    }
  };

  const markAsTodo = async () => {
    try {
      await axios.patch(`${API}/progress/${user.id}/${exerciseId}?status=todo`);
      toast.success('Moved back to To Do!');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update');
    }
  };

  const togglePlay = (index) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    // Pause all other audio
    audioRefs.current.forEach((ref, i) => {
      if (i !== index && ref) {
        ref.pause();
      }
    });

    if (playingIndex === index) {
      audio.pause();
      setPlayingIndex(null);
    } else {
      audio.play();
      setPlayingIndex(index);
    }
  };

  const renderTextWithColumns = (text) => {
    if (!text) return null;

    // Check if text contains pipe separators for table layout
    const lines = text.split('\n').filter(line => line.trim());
    const hasPipes = lines.some(line => line.includes('|'));

    if (hasPipes) {
      // Table/spreadsheet layout with | separators
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <tbody>
              {lines.map((line, rowIndex) => {
                const cells = line.split('|').map(cell => cell.trim());
                return (
                  <tr key={rowIndex}>
                    {cells.map((cell, cellIndex) => (
                      <td 
                        key={cellIndex} 
                        className="border border-gray-300 px-4 py-3 text-base leading-loose text-gray-800"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // Check for ||| separator (original two-column layout)
    const columns = text.split('|||').map(col => col.trim()).filter(col => col);

    if (columns.length === 1) {
      // Single column
      return (
        <div className="whitespace-pre-wrap text-base leading-loose text-gray-800">
          {columns[0]}
        </div>
      );
    } else {
      // Multiple columns (||| separator)
      return (
        <div className="grid gap-8" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
          {columns.map((column, index) => (
            <div key={index} className="whitespace-pre-wrap text-base leading-loose text-gray-800">
              {column}
            </div>
          ))}
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Exercise not found</p>
          <Button onClick={() => navigate('/')}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 px-4" data-testid="exercise-view">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Board
        </Button>

        {/* Exercise Info */}
        <Card className="mb-6">
          <CardHeader>
            <div className="text-sm text-gray-500 mb-1">{lesson?.title}</div>
            <CardTitle className="text-3xl" data-testid="exercise-title">{exercise.title}</CardTitle>
            <p className="text-gray-600 mt-2" data-testid="exercise-description">{exercise.description}</p>
          </CardHeader>
        </Card>

        {/* Practice Text with Column Support */}
        {exercise.text_content && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Practice Text</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="bg-white p-6 rounded-lg border-2 border-gray-200"
                data-testid="practice-text-content"
              >
                {renderTextWithColumns(exercise.text_content)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audio Players */}
        {exercise.audio_files && exercise.audio_files.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                Audio Files ({exercise.audio_files.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {exercise.audio_files.map((filename, index) => {
                  const label = exercise.audio_labels && exercise.audio_labels[index] 
                    ? exercise.audio_labels[index] 
                    : `Audio ${index + 1}`;
                  return (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <audio
                        ref={el => audioRefs.current[index] = el}
                        src={`${API}/files/audio/${filename}`}
                        onEnded={() => setPlayingIndex(null)}
                        data-testid={`audio-player-${index}`}
                      />
                      <Button
                        onClick={() => togglePlay(index)}
                        size="lg"
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        data-testid={`play-pause-btn-${index}`}
                      >
                        {playingIndex === index ? (
                          <><Pause className="w-5 h-5 mr-2" /> Pause</>
                        ) : (
                          <><Play className="w-5 h-5 mr-2" /> Play</>
                        )}
                      </Button>
                      <span className="text-sm text-gray-600 truncate flex-1">
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Images */}
        {exercise.image_files && exercise.image_files.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Reference Images
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exercise.image_files.map((filename, index) => (
                  <img
                    key={index}
                    src={`${API}/files/image/${filename}`}
                    alt={`Reference image ${index + 1}`}
                    className="w-full rounded-lg shadow-md"
                    data-testid={`exercise-image-${index}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 justify-center">
              {exerciseStatus === 'todo' ? (
                <Button
                  onClick={markAsDone}
                  size="lg"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8"
                  data-testid="mark-done-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Mark as Done
                </Button>
              ) : (
                <Button
                  onClick={markAsTodo}
                  size="lg"
                  variant="outline"
                  className="px-8"
                  data-testid="mark-todo-btn"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Move to To Do
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                size="lg"
                variant="outline"
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
